import type { OnInit } from '@angular/core';
import type { WebLegacyWidgetEntity } from '@web/types';

import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { ERadioGroupType } from '@ui/sp-radio-group';
import { SpEditorService } from '@components/sp-editor/service/sp-editor.service';

import type { ExtendedPositionLegacy, TextEntity, WidgetPositionLegacy } from '@sites/entity-core';
import type { TextForm, TextZeroForm } from '@sites/entity-signal-form';

import { LayoutFacade } from '@web/store/layout/layout.facade';
import { WidgetsFacade } from '@web/store/page-structure/widgets/widgets.facade';
import { WebLegacyWidgetSignalFormService } from '@web/core/services/web-legacy-widget-signal-form.service';

import { CommonElementsModule } from '@web/widgets/common/common-elements.module';
import { WebWidgetHideSettingsComponent } from '@web/shared/components/web-widget-hide-settings';
import { WebWidgetPositionSettingsComponent } from '@web/shared/components/web-widget-position-settings';
import { WebWidgetExtendedHideSettingsComponent } from '@web/shared/components/web-widget-extended-hide-settings';
import { SpExtendedPositionSignalSettingsComponent } from '@components/sp-extended-position-signal-settings';
import { SpAnimationSettingsSignalLegacyComponent } from '@components/sp-animation-settings-signal-legacy';

@Component({
    selector: 'web-text-control',
    templateUrl: './text-control.component.html',
    styleUrls: ['./text-control.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonElementsModule,
        WebWidgetPositionSettingsComponent,
        WebWidgetHideSettingsComponent,
        WebWidgetExtendedHideSettingsComponent,
        SpExtendedPositionSignalSettingsComponent,
        SpAnimationSettingsSignalLegacyComponent,
    ],
})
export class TextControlComponent implements OnInit {
    public readonly widget = input.required<WebLegacyWidgetEntity<TextEntity['data']>>();
    public readonly isSpecific = input<boolean>(false);
    public readonly isEduSite = input<boolean>(false);

    private readonly signalFormService: WebLegacyWidgetSignalFormService = inject(WebLegacyWidgetSignalFormService);
    private readonly widgetsFacade: WidgetsFacade = inject(WidgetsFacade);
    private readonly layoutFacade: LayoutFacade = inject(LayoutFacade);
    private readonly spEditorService: SpEditorService = inject(SpEditorService);
    private readonly destroyRef: DestroyRef = inject(DestroyRef);

    public readonly ERadioGroupType = ERadioGroupType;

    public readonly isDeviceDesktopSig = toSignal(this.layoutFacade.isDeviceDesktop$, { initialValue: true });
    public readonly isDeviceTabletSig = toSignal(this.layoutFacade.isDeviceTablet$, { initialValue: false });
    public readonly isDeviceMobileSig = toSignal(this.layoutFacade.isDeviceMobile$, { initialValue: false });

    public form!: TextForm;

    public ngOnInit(): void {
        this.form = this.signalFormService.getOrCreateScoped<TextForm>(this.widget().id, this.destroyRef);
    }

    /** True when the widget lives inside a zero block — drives template branching. */
    public get isZero(): boolean {
        return this.widget().type === 'zero';
    }

    /** The form narrowed to the zero variant. */
    public get zeroForm(): TextZeroForm {
        return this.form as TextZeroForm;
    }

    /** The default-placement form (`position` is the simple per-device grid). */
    public get defaultForm(): TextForm {
        return this.form;
    }

    public setHorizontalAlign(value: string): void {
        this.form.setHorizontalAlign(value as TextEntity['data']['horizontalAlign']);
    }

    public setSynchronizeStyle(): void {
        this.form.setIsSynchronizeStyle(!this.form.isSynchronized());
    }

    public setHideOnMobile(value: boolean): void {
        this.form.setHideOnMobile(value);
    }

    public setHideOnTablet(value: boolean): void {
        this.form.setHideOnTablet(value);
    }

    public setHideOnDesktop(value: boolean): void {
        this.form.setHideOnDesktop(value);
    }

    public updateWidgetDisplayId(displayId: string): void {
        this.widgetsFacade.updateWidgetDisplayId(this.widget().id, displayId);
    }

    /** Click-on-panel blurs the rich-text editor (the FormGroup base's `blurEditor`). */
    public blurEditor(): void {
        this.spEditorService.simulateBlur();
    }

    // --- Layout (default variant) ---

    public readonly defaultPosition = computed<WidgetPositionLegacy | null>(() => {
        const f = this.form as TextForm | undefined;
        if (!f || this.isZero) return null;
        return (f.position().value() as WidgetPositionLegacy) ?? null;
    });

    public onDefaultPositionChange(patch: Partial<WidgetPositionLegacy>): void {
        const current = this.defaultForm.position().value();
        this.defaultForm.setPosition({ ...current, ...patch });
    }

    // --- Layout (zero variant) ---

    public readonly zeroPosition = computed<ExtendedPositionLegacy | null>(() => {
        const f = this.form as TextZeroForm | undefined;
        if (!f || !this.isZero) return null;
        return f.position().value() as ExtendedPositionLegacy;
    });
}
